// Plan Management — the section 15 reports.
//
// These are operational, not analytical: every panel exists because somebody has to DO something
// today. So each one leads with the single figure that demands action, at page scale, and puts the
// supporting table underneath it. Nothing here mutates; the only write is a CSV download.
//
// Why a rail instead of one long stacked page: the seven answer different questions on different
// days ("are we meeting the commitment?", "what has stalled?", "what cannot be paid?"). Stacking
// would bury the one being asked about under six that are not.
//
// House rules from ui.jsx are kept: index.css component classes, slate surface, colour reserved for
// STATE (rose = overdue/at risk · amber = attention · emerald = healthy · cyan = reconciled),
// primary for selection only, money tabular and right-aligned via the kit's Currency/money.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  AlertOctagon, AlertTriangle, Banknote, Building2, CheckCircle2, Download, FileSearch,
  FileWarning, Hourglass, Inbox, Info, Loader2, Play, RefreshCw, Scale, Timer,
} from 'lucide-react';
import {
  Currency, DataTable, EmptyState, ErrorState, Field, FormRow, KpiTile, MicroLabel, MoneyStat,
  SectionCard, StatRow, StatusBadge, TONE_TEXT, TableSkeleton, fmtDate, fmtDateTime, money,
} from './ui';

const PM = '/plan-management';

const int = (n) => Number(n || 0).toLocaleString('en-AU');
// Averages arrive rounded to 1dp from the server; keep the decimal so "0.4 days" never reads "0".
const days1 = (n) => (n == null ? '—' : Number(n).toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const pct1 = (n) => (n == null ? '—' : `${Number(n).toLocaleString('en-AU', { maximumFractionDigits: 1 })}%`);

// Surface the API's own message verbatim. The PM role gate and the audit-export guard answer with
// text written for the operator ("Narrow the export: pass a participant, provider, invoice or date
// range."). Replacing that with a generic "Forbidden" would hide what is actually required.
const apiMessage = (e, fallback = 'Could not load this report.') => {
  const m = e && e.response && e.response.data && e.response.data.message;
  if (m) return m;
  if (e && e.response && e.response.status === 403) return 'You do not have permission to view this report.';
  return (e && e.message) || fallback;
};

// An error response for a blob request arrives as a Blob, so the JSON message has to be read out
// of it or the operator sees nothing but "Request failed with status code 400".
const blobMessage = async (e, fallback = 'Export failed.') => {
  const d = e && e.response && e.response.data;
  if (d && typeof Blob !== 'undefined' && d instanceof Blob) {
    try {
      const j = JSON.parse(await d.text());
      if (j && j.message) return j.message;
    } catch (notJson) { /* the body was a real file, or unparseable — fall through */ }
  }
  return apiMessage(e, fallback);
};

// Turn whatever a panel is currently SHOWING into a CSV. Every report had a number on screen and
// no way to get it out — six of the seven could not be exported at all, so the only way to hand a
// figure to an accountant or an auditor was to retype it.
//
// It exports the rows as displayed, after the panel's own filters, so the file and the screen can
// never be two different stories.
const csvEscape = (v) => {
  const t = v == null ? '' : String(v);
  return /[",\r\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
};
function exportRows(filename, columns, rows) {
  if (!rows || !rows.length) return;
  const head = columns.map((c) => csvEscape(c.header)).join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape(typeof c.value === 'function' ? c.value(r) : r[c.value])).join(','));
  downloadBlob([head, ...body].join('\r\n'), filename);
}
// One button, same place on every report, disabled when there is nothing to write.
function ExportButton({ filename, columns, rows, label = 'Export CSV' }) {
  const n = (rows || []).length;
  return (
    <button type="button" onClick={() => exportRows(filename, columns, rows)} disabled={!n}
      title={n ? `Download these ${n} row${n === 1 ? '' : 's'} as CSV` : 'Nothing to export yet'}
      className="btn-secondary btn-sm disabled:opacity-40 inline-flex items-center gap-1.5">
      <Download size={13} />{label}
    </button>
  );
}

const downloadBlob = (data, name, type = 'text/csv') => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

// One fetch shape for every panel. `nonce` lets a panel re-run the same query on demand (the audit
// export needs that — the filters may not have changed, but the trail has).
function useReport(url, { params, enabled = true, nonce = 0 } = {}) {
  const [state, setState] = useState({ loading: enabled, error: null, data: null });
  const key = JSON.stringify(params || {});
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = seq.current + 1;
    seq.current = my;
    if (!enabled) { setState({ loading: false, error: null, data: null }); return; }
    setState((s) => ({ loading: true, error: null, data: s.data }));
    try {
      const r = await api.get(url, { params: JSON.parse(key) });
      if (seq.current === my) setState({ loading: false, error: null, data: r.data });
    } catch (e) {
      // Stale responses are dropped, so an abandoned filter can never overwrite a newer result.
      if (seq.current === my) setState({ loading: false, error: apiMessage(e), data: null });
    }
  }, [url, key, enabled]);

  useEffect(() => { load(); }, [load, nonce]);
  return { ...state, reload: load };
}

/* ── local primitives ──────────────────────────────────────────────────────
   Deliberately few. Anything that already exists in ui.jsx is used as-is; these
   three are the shapes the kit does not have: a page-scale hero figure, a
   proportional stage bar, and a share-of-total bar. */

const HERO_TONE = {
  slate: 'text-slate-900', emerald: 'text-emerald-600', amber: 'text-amber-600',
  rose: 'text-rose-600', cyan: 'text-cyan-600',
};
const HERO_EDGE = {
  slate: 'border-slate-200', emerald: 'border-emerald-200', amber: 'border-amber-200',
  rose: 'border-rose-200', cyan: 'border-cyan-200',
};

// The figure the panel exists for. Everything else is support, so this gets the size and the only
// strong colour on the page.
function Hero({ label, value, tone = 'slate', sub, hint, icon: Icon, aside }) {
  return (
    <div className={clsx('bg-white border rounded-xl px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-4', HERO_EDGE[tone] || HERO_EDGE.slate)}>
      <div className="min-w-0">
        <MicroLabel className="flex items-center gap-1.5">
          {Icon && <Icon size={12} className={clsx(TONE_TEXT[tone] || 'text-slate-400')} />}{label}
        </MicroLabel>
        <div className={clsx('text-[38px] leading-none font-bold tabular-nums mt-2', HERO_TONE[tone] || HERO_TONE.slate)}>{value}</div>
        {sub && <p className="text-[13px] text-slate-600 mt-2">{sub}</p>}
        {hint && <p className="text-[11.5px] text-slate-400 mt-1 max-w-lg">{hint}</p>}
      </div>
      {aside && <div className="ml-auto min-w-0">{aside}</div>}
    </div>
  );
}

// Stage average drawn against the slowest stage, not against the target — the useful reading here
// is "which step is the bottleneck", and that is the one the operator can go and shorten.
function StageBar({ label, value, max, why, slowest }) {
  const pct = max > 0 && value != null ? Math.max(2, (Number(value) / max) * 100) : 0;
  return (
    <div className="py-2.5">
      <div className="flex items-baseline gap-3">
        <span className="text-[12.5px] font-medium text-slate-700 truncate">{label}</span>
        {slowest && <span className="badge badge-yellow !text-[10px] shrink-0">Slowest stage</span>}
        <span className={clsx('ml-auto text-[13px] font-semibold tabular-nums whitespace-nowrap', slowest ? 'text-amber-600' : 'text-slate-800')}>
          {days1(value)} <span className="text-[11px] font-normal text-slate-400">business days</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
        <div className={clsx('h-full rounded-full', slowest ? 'bg-amber-500' : 'bg-slate-300')} style={{ width: `${pct}%` }} />
      </div>
      {why && <p className="text-[11.5px] text-slate-400 mt-1">{why}</p>}
    </div>
  );
}

const SHARE_BAR = { amber: 'bg-amber-500', rose: 'bg-rose-500', emerald: 'bg-emerald-500', slate: 'bg-slate-300' };
const SHARE_TXT = {
  amber: 'text-amber-600 font-semibold', rose: 'text-rose-600 font-semibold',
  emerald: 'text-emerald-600 font-semibold', slate: 'text-slate-400',
};

// Share-of-total. Neutral by default; it only takes colour once one group is large enough to be a
// systemic cause rather than a coincidence of small numbers. `tone` overrides that for the columns
// where a large share is the GOOD outcome (a high re-claim success rate must not read as a warning).
function ShareBar({ value, total, tone, className }) {
  const n = total > 0 ? Math.min(100, (Number(value || 0) / total) * 100) : 0;
  const t = tone || (total >= 3 && n >= 50 ? 'amber' : 'slate');
  return (
    <div className={clsx('flex items-center gap-2 justify-end', className)}>
      <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden shrink-0">
        <div className={clsx('h-full rounded-full', SHARE_BAR[t] || SHARE_BAR.slate)} style={{ width: `${n}%` }} />
      </div>
      <span className={clsx('text-[11px] tabular-nums w-9 text-right', SHARE_TXT[t] || SHARE_TXT.slate)}>{Math.round(n)}%</span>
    </div>
  );
}

// Business days waiting. The threshold is the whole point of the column, so past it the number is
// rose — there is no "slightly overdue" reading to preserve.
function WaitDays({ days, threshold = 5 }) {
  const n = Number(days || 0);
  return (
    <span className={clsx('tabular-nums font-semibold', n > threshold ? 'text-rose-600' : 'text-slate-500')}>
      {int(n)}
    </span>
  );
}

const PILL = { emerald: 'badge-green', amber: 'badge-yellow', rose: 'badge-red', cyan: 'badge-cyan', slate: 'badge-gray' };
function Pill({ tone = 'slate', children }) {
  return <span className={PILL[tone] || PILL.slate}>{children}</span>;
}

// The backend hands back a `note` on several of these explaining what the numbers mean. It is
// written for the operator, so it is shown rather than dropped.
function Footnote({ children }) {
  if (!children) return null;
  return (
    <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-slate-500">
      <Info size={12} className="mt-0.5 shrink-0 text-slate-300" />
      <span>{children}</span>
    </p>
  );
}

function Callout({ tone = 'amber', title, children, icon: Icon = AlertTriangle }) {
  const edge = { amber: 'border-amber-200 bg-amber-50/60', rose: 'border-rose-200 bg-rose-50/60', emerald: 'border-emerald-200 bg-emerald-50/60' };
  return (
    <div className={clsx('rounded-xl border px-4 py-3 flex items-start gap-2.5', edge[tone] || edge.amber)}>
      <Icon size={15} className={clsx('mt-0.5 shrink-0', TONE_TEXT[tone])} />
      <div className="min-w-0">
        <p className={clsx('text-[13px] font-semibold', TONE_TEXT[tone])}>{title}</p>
        <div className="text-[12.5px] text-slate-600 mt-0.5">{children}</div>
      </div>
    </div>
  );
}

// Panel chrome + the three states every panel must handle. `isEmpty` is separate from `error` on
// purpose: several of these are legitimately empty on a healthy install, and an empty report must
// read as "nothing to action", not as breakage.
function Panel({ title, blurb, actions, loading, error, onRetry, isEmpty, empty, children }) {
  return (
    <section className="min-w-0 space-y-4" aria-label={title}>
      <header className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold text-slate-900">{title}</h2>
          {blurb && <p className="text-[12.5px] text-slate-500 mt-0.5 max-w-2xl">{blurb}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {actions}
          {onRetry && (
            <button type="button" onClick={onRetry} disabled={loading} className="btn-secondary btn-sm">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}Refresh
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <SectionCard><TableSkeleton rows={7} cols={5} /></SectionCard>
      ) : error ? (
        <SectionCard><ErrorState message={error} onRetry={onRetry} /></SectionCard>
      ) : isEmpty ? (
        <SectionCard>
          <EmptyState icon={(empty && empty.icon) || CheckCircle2} title={(empty && empty.title) || 'Nothing to action'} hint={empty && empty.hint} />
        </SectionCard>
      ) : children}
    </section>
  );
}

/* ── 1 · Turnaround ────────────────────────────────────────────────────────
   The flagship. The operation is measured in BUSINESS days against the 5-to-8
   business day commitment made to every provider on receipt, so the commitment
   result is the hero and the unit is spelled out everywhere it appears —
   measuring the same pipeline in calendar days quietly flatters it by ~40%. */

const STAGES = [
  { key: 'received_to_entered', label: 'Received → Entered', why: 'How long an invoice sits before it is on the system at all.' },
  { key: 'entered_to_approved', label: 'Entered → Approved', why: 'Waiting on the participant or their delegate to approve.' },
  { key: 'approved_to_claimed', label: 'Approved → Claimed', why: 'Waiting for the next claim batch to go to the NDIA.' },
  { key: 'claimed_to_reconciled', label: 'Claimed → Reconciled', why: 'NDIA turnaround. Outside your control, not outside your reporting.' },
  { key: 'reconciled_to_paid', label: 'Reconciled → Provider paid', why: 'Your payment run once the NDIA money has landed.' },
];

const commitmentTone = (p) => (p == null ? 'slate' : p >= 90 ? 'emerald' : p >= 75 ? 'amber' : 'rose');

function TurnaroundPanel() {
  const [draft, setDraft] = useState({ from: '', to: '' });
  const [range, setRange] = useState({ from: '', to: '' });
  // Undefined rather than '' so axios drops the key entirely — the route treats a null date as
  // "no bound", and an empty string would be parsed as an invalid date.
  const params = useMemo(() => ({ from: range.from || undefined, to: range.to || undefined }), [range]);
  const { loading, error, data, reload } = useReport(`${PM}/reports/turnaround`, { params });

  const stages = (data && data.stages_business_days) || {};
  const commit = (data && data.commitment) || {};
  const maxStage = Math.max(0, ...STAGES.map((s) => Number(stages[s.key] || 0)));
  const slowestKey = maxStage > 0 ? (STAGES.find((s) => Number(stages[s.key] || 0) === maxStage) || {}).key : null;
  const target = commit.target_business_days || 8;
  const endToEnd = stages.received_to_paid;

  const filters = (
    <form
      className="flex items-end gap-2 flex-wrap"
      onSubmit={(e) => { e.preventDefault(); setRange(draft); }}>
      <Field label="From" className="w-36">
        {(id) => <input id={id} type="date" value={draft.from} max={draft.to || undefined}
          onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} className="input-field !h-8 !py-1 !text-[13px]" />}
      </Field>
      <Field label="To" className="w-36">
        {(id) => <input id={id} type="date" value={draft.to} min={draft.from || undefined}
          onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} className="input-field !h-8 !py-1 !text-[13px]" />}
      </Field>
      <button type="submit" className="btn-secondary btn-sm">Apply</button>
      {(range.from || range.to) && (
        <button type="button" className="btn-ghost btn-sm" onClick={() => { setDraft({ from: '', to: '' }); setRange({ from: '', to: '' }); }}>Clear</button>
      )}
    </form>
  );

  return (
    <Panel
      title="Turnaround"
      blurb={`Business days per stage, measured from the append-only audit trail, against the 5-to-8 business day commitment made to every provider on receipt.`}
      actions={(
        <>
          {filters}
          <ExportButton filename={`pm-turnaround-${new Date().toISOString().slice(0, 10)}.csv`}
            rows={STAGES.map((st) => ({ stage: st.label, days: stages[st.key] }))}
            columns={[{ header: 'Stage', value: 'stage' }, { header: 'Business days', value: 'days' }]} />
        </>
      )}
      loading={loading} error={error} onRetry={reload}
      isEmpty={!!data && !data.invoices}
      empty={{ icon: Inbox, title: 'No invoices received in this range', hint: 'Widen the dates, or clear them to measure everything on record.' }}>
      {data && (
        <div className="space-y-4">
          <Hero
            icon={Timer}
            label={`Paid within ${target} business days`}
            value={commit.pct == null ? '—' : pct1(commit.pct)}
            tone={commitmentTone(commit.pct)}
            sub={commit.pct == null
              ? 'No invoice in this range has completed the whole path yet, so there is nothing to measure against the commitment.'
              : `${int(commit.met)} met · ${int(commit.missed)} missed, of ${int(data.completed)} invoices paid end to end.`}
            hint="Business days exclude weekends. Measuring the same pipeline in calendar days flatters every figure here by roughly 40%."
            aside={(
              <StatRow cols={3} className="min-w-[15rem]">
                <MoneyStat label="Invoices received" value={int(data.invoices)} currency={false} />
                <MoneyStat label="Completed" value={int(data.completed)} currency={false} />
                <MoneyStat
                  label="End to end (avg)" currency={false}
                  value={endToEnd == null ? '—' : `${days1(endToEnd)}d`}
                  tone={endToEnd == null ? 'slate' : endToEnd <= target ? 'emerald' : 'rose'}
                  sub="business days"
                />
              </StatRow>
            )}
          />

          <SectionCard title="Where the time goes" icon={Timer} bodyClass="px-5 py-2 divide-y divide-slate-100">
            {STAGES.map((s) => (
              <StageBar key={s.key} label={s.label} value={stages[s.key]} max={maxStage} why={s.why} slowest={s.key === slowestKey} />
            ))}
            <div className="py-3 flex items-baseline gap-3">
              <span className="text-[12.5px] font-semibold text-slate-800">Received → Provider paid</span>
              <span className="text-[11.5px] text-slate-400">the whole path, not the sum of the averages above</span>
              <span className={clsx('ml-auto text-[15px] font-bold tabular-nums whitespace-nowrap',
                endToEnd == null ? 'text-slate-400' : endToEnd <= target ? 'text-emerald-600' : 'text-rose-600')}>
                {days1(endToEnd)} <span className="text-[11px] font-normal text-slate-400">business days</span>
              </span>
            </div>
          </SectionCard>

          <Footnote>{data.note}</Footnote>
        </div>
      )}
    </Panel>
  );
}

/* ── 2 · Aged work ─────────────────────────────────────────────────────────
   Money sitting still that nobody is being told about. Anything past 5 business
   days is the threshold spec 15 sets, so it is flagged in the number, in the
   row accent and in the count above the table. */

const AGED_THRESHOLD = 5;

function AgedPanel() {
  const { loading, error, data, reload } = useReport(`${PM}/reports/aged`);
  const unclaimed = (data && data.unclaimed_approved) || { count: 0, total: 0, invoices: [] };
  const claims = (data && data.aged_claims) || { count: 0, overdue: 0, invoices: [] };
  const unclaimedOverdue = (unclaimed.invoices || []).filter((r) => Number(r.business_days_waiting) > AGED_THRESHOLD).length;

  const waitCol = {
    key: 'business_days_waiting', header: `Waiting (business days)`, align: 'right', sortable: true, width: 170,
    sortValue: (r) => Number(r.business_days_waiting || 0),
    render: (r) => <WaitDays days={r.business_days_waiting} threshold={AGED_THRESHOLD} />,
  };
  const overdueTone = (r) => (Number(r.business_days_waiting) > AGED_THRESHOLD ? 'rose' : undefined);

  return (
    <Panel
      title="Aged work"
      actions={(
        <ExportButton filename={`pm-aged-${new Date().toISOString().slice(0, 10)}.csv`} rows={unclaimed.invoices || []}
          columns={[
            { header: 'Invoice', value: 'provider_invoice_number' },
            { header: 'Participant', value: 'participant' },
            { header: 'Provider', value: 'provider' },
            { header: 'Approved', value: 'approval_at' },
            { header: 'Amount', value: 'total_amount' },
          ]} />
      )}
      blurb="Invoices that have stopped moving: approved but never put in a claim, and claims with no result back from the NDIA. Anything past 5 business days is overdue."
      loading={loading} error={error} onRetry={reload}
      isEmpty={!!data && !unclaimed.count && !claims.count}
      empty={{ title: 'Nothing is stalled', hint: 'Every approved invoice is in a claim, and every claim has a result back from the NDIA.' }}>
      {data && (
        <div className="space-y-4">
          <Hero
            icon={Hourglass}
            label="Approved, not yet claimed"
            value={money(unclaimed.total)}
            tone={unclaimedOverdue ? 'rose' : unclaimed.count ? 'amber' : 'emerald'}
            sub={unclaimed.count
              ? `${int(unclaimed.count)} invoices are approved and eligible to claim. ${int(unclaimedOverdue)} have waited more than ${AGED_THRESHOLD} business days.`
              : 'Every approved invoice is already in a claim batch.'}
            hint="This is participant money already committed, not yet asked for. It cannot reach the provider until it is claimed."
            aside={(
              <StatRow cols={2} className="min-w-[13rem]">
                <MoneyStat label="Claims awaiting a result" value={int(claims.count)} currency={false} />
                <MoneyStat label={`Overdue past ${AGED_THRESHOLD} days`} value={int(claims.overdue)} currency={false}
                  tone={claims.overdue ? 'rose' : 'emerald'} />
              </StatRow>
            )}
          />

          <SectionCard title={`Approved, not claimed · ${int(unclaimed.count)}`} icon={Hourglass}>
            <DataTable
              label="Approved invoices not yet claimed"
              rows={unclaimed.invoices || []}
              rowTone={overdueTone}
              defaultSort={{ key: 'business_days_waiting', dir: 'desc' }}
              maxHeight="26rem"
              empty={{ icon: CheckCircle2, title: 'Nothing waiting', hint: 'Every approved invoice is in a claim batch.' }}
              columns={[
                { key: 'provider_invoice_number', header: 'Invoice', sortable: true, width: 150, render: (r) => r.provider_invoice_number || <span className="text-slate-300">—</span> },
                { key: 'participant', header: 'Participant', sortable: true, truncate: true },
                { key: 'provider', header: 'Provider', sortable: true, truncate: true, render: (r) => r.provider || <span className="text-slate-300">—</span> },
                { key: 'approval_at', header: 'Approved', sortable: true, width: 130, render: (r) => <span className="text-slate-500">{fmtDate(r.approval_at || r.updated_at)}</span> },
                { key: 'total_amount', header: 'Amount', money: true, sortable: true, width: 120, sortValue: (r) => Number(r.total_amount || 0) },
                waitCol,
              ]}
              footer={(
                <span className="flex items-center gap-2">
                  <span className="tabular-nums font-semibold text-slate-700">{money(unclaimed.total)}</span>
                  <span className="text-slate-400">approved and claimable</span>
                </span>
              )}
            />
          </SectionCard>

          <SectionCard title={`Claimed, no result back · ${int(claims.count)}`} icon={AlertOctagon}>
            <DataTable
              label="Claims awaiting an NDIA result"
              rows={claims.invoices || []}
              rowTone={overdueTone}
              defaultSort={{ key: 'business_days_waiting', dir: 'desc' }}
              maxHeight="26rem"
              empty={{ icon: CheckCircle2, title: 'No claim is waiting', hint: 'Every submitted claim has come back from the NDIA.' }}
              columns={[
                { key: 'provider_invoice_number', header: 'Invoice', sortable: true, width: 150, render: (r) => r.provider_invoice_number || <span className="text-slate-300">—</span> },
                { key: 'participant', header: 'Participant', sortable: true, truncate: true },
                { key: 'batch_ref', header: 'Batch', sortable: true, width: 150, render: (r) => (r.batch_ref ? <span className="font-mono text-[12px] text-slate-600">{r.batch_ref}</span> : <span className="text-slate-300">—</span>) },
                { key: 'submitted_at', header: 'Submitted', sortable: true, width: 130, render: (r) => <span className="text-slate-500">{fmtDate(r.submitted_at)}</span> },
                { key: 'ndia_status', header: 'NDIA status', width: 130, render: (r) => (r.ndia_status ? <Pill tone="slate">{r.ndia_status}</Pill> : <span className="text-slate-300">Awaiting</span>) },
                { key: 'total_amount', header: 'Amount', money: true, sortable: true, width: 120, sortValue: (r) => Number(r.total_amount || 0) },
                waitCol,
              ]}
            />
          </SectionCard>

          <Footnote>
            Both lists are capped at the 200 oldest. Waiting time runs from approval (or the claim submission)
            to today, counting business days only.
          </Footnote>
        </div>
      )}
    </Panel>
  );
}

/* ── 3 · Claim errors ──────────────────────────────────────────────────────
   The point of this panel is spotting a SYSTEMIC fault — one provider's ABN,
   one bad item number — rather than reading a run of unrelated one-offs. So it
   leads with the grouping and with an explicit concentration check; a flat list
   of failed invoices is what hides the pattern. */

function ClaimErrorsPanel() {
  const { loading, error, data, reload } = useReport(`${PM}/reports/claim-errors`);
  const byReason = (data && data.by_reason) || [];
  const byProvider = (data && data.by_provider) || [];
  const attempts = (data && data.reclaim_outcomes) || [];

  const totalInv = byReason.reduce((s, r) => s + Number(r.invoices || 0), 0);
  const totalVal = byReason.reduce((s, r) => s + Number(r.value || 0), 0);
  // Rows arrive already ordered by invoice count, so the head of each list is the candidate cause.
  const topReason = byReason[0];
  const topProvider = byProvider[0];
  const share = (n) => (totalInv > 0 ? (Number(n || 0) / totalInv) * 100 : 0);
  // Three is the floor for calling anything systemic — 1-of-1 is 100% and means nothing.
  const systemicReason = topReason && totalInv >= 3 && share(topReason.invoices) >= 50 ? topReason : null;
  const systemicProvider = topProvider && totalInv >= 3 && share(topProvider.invoices) >= 50 ? topProvider : null;

  const reclaimed = attempts.reduce((s, a) => s + Number(a.lines || 0), 0);
  const reclaimOk = attempts.reduce((s, a) => s + Number(a.succeeded || 0), 0);

  return (
    <Panel
      title="Claim errors"
      actions={(
        <ExportButton filename={`pm-claim-errors-${new Date().toISOString().slice(0, 10)}.csv`} rows={(data && data.by_reason) || []}
          columns={[
            { header: 'Reason', value: 'reason' },
            { header: 'Invoices', value: 'invoices' },
            { header: 'Value', value: 'value' },
          ]} />
      )}
      blurb="Rejected and errored claims grouped by reason and by provider, so one recurring cause is visible instead of looking like a run of unrelated one-offs."
      loading={loading} error={error} onRetry={reload}
      isEmpty={!!data && !byReason.length && !byProvider.length && !attempts.length}
      empty={{ title: 'No claim errors on record', hint: 'Nothing has come back from the NDIA rejected or errored.' }}>
      {data && (
        <div className="space-y-4">
          <Hero
            icon={AlertOctagon}
            label="Invoices in claim error or held"
            value={int(totalInv)}
            tone={totalInv ? 'rose' : 'emerald'}
            sub={totalInv
              ? (
                <span>
                  Worth <span className="font-semibold tabular-nums">{money(totalVal)}</span> across {int(byReason.length)} distinct{' '}
                  {byReason.length === 1 ? 'reason' : 'reasons'} and {int(byProvider.length)} {byProvider.length === 1 ? 'provider' : 'providers'}.
                </span>
              )
              : 'Nothing is sitting in claim error.'}
            hint="Correct the cause, then re-claim in a new batch. The original claim reference stays on record."
            aside={reclaimed > 0 ? (
              <StatRow cols={2} className="min-w-[13rem]">
                <MoneyStat label="Re-claimed lines" value={int(reclaimed)} currency={false} />
                <MoneyStat label="Succeeded on retry" value={pct1(Math.round((reclaimOk / reclaimed) * 1000) / 10)} currency={false}
                  tone={reclaimOk / reclaimed >= 0.8 ? 'emerald' : 'amber'} />
              </StatRow>
            ) : null}
          />

          {(systemicReason || systemicProvider) && (
            <Callout title="Likely systemic, not a run of one-offs">
              {systemicReason && (
                <p>
                  <span className="font-semibold text-slate-700">{systemicReason.reason}</span>
                  {systemicReason.code && systemicReason.code !== 'unmapped' ? <span className="text-slate-400"> ({systemicReason.code})</span> : null}
                  {' '}accounts for {int(systemicReason.invoices)} of {int(totalInv)} errored invoices ({Math.round(share(systemicReason.invoices))}%). Fix the cause once and the whole group clears.
                </p>
              )}
              {systemicProvider && (
                <p className={systemicReason ? 'mt-1' : undefined}>
                  <span className="font-semibold text-slate-700">{systemicProvider.provider || 'One provider'}</span>
                  {' '}is behind {int(systemicProvider.invoices)} of {int(totalInv)} ({Math.round(share(systemicProvider.invoices))}%) — check their ABN, registration and item numbers before re-claiming any of them.
                </p>
              )}
            </Callout>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <SectionCard title="By reason" icon={AlertTriangle}>
              <DataTable
                label="Claim errors by reason" rows={byReason} rowKey={(r, i) => `${r.code}-${i}`}
                rowTone={(r) => (systemicReason && r === systemicReason ? 'amber' : undefined)}
                empty={{ icon: CheckCircle2, title: 'No errors to group' }}
                columns={[
                  {
                    key: 'reason', header: 'Reason', sortable: true, truncate: true,
                    render: (r) => (
                      <div className="min-w-0">
                        <div className="text-slate-800 truncate" title={r.reason}>{r.reason}</div>
                        {r.code && r.code !== 'unmapped' && <div className="text-[11px] font-mono text-slate-400 mt-0.5">{r.code}</div>}
                      </div>
                    ),
                  },
                  { key: 'invoices', header: 'Invoices', align: 'right', sortable: true, width: 90, render: (r) => <span className="tabular-nums font-semibold text-slate-800">{int(r.invoices)}</span> },
                  { key: 'share', header: 'Share', align: 'right', width: 120, render: (r) => <ShareBar value={r.invoices} total={totalInv} /> },
                  { key: 'value', header: 'Value', money: true, sortable: true, width: 120, sortValue: (r) => Number(r.value || 0) },
                ]}
              />
            </SectionCard>

            <SectionCard title="By provider" icon={Building2}>
              <DataTable
                label="Claim errors by provider" rows={byProvider} rowKey={(r, i) => `${r.provider || 'unknown'}-${i}`}
                rowTone={(r) => (systemicProvider && r === systemicProvider ? 'amber' : undefined)}
                empty={{ icon: CheckCircle2, title: 'No errors to group' }}
                columns={[
                  { key: 'provider', header: 'Provider', sortable: true, truncate: true, render: (r) => r.provider || <span className="text-slate-300">Unnamed provider</span> },
                  { key: 'invoices', header: 'Invoices', align: 'right', sortable: true, width: 90, render: (r) => <span className="tabular-nums font-semibold text-slate-800">{int(r.invoices)}</span> },
                  { key: 'share', header: 'Share', align: 'right', width: 120, render: (r) => <ShareBar value={r.invoices} total={totalInv} /> },
                  { key: 'value', header: 'Value', money: true, sortable: true, width: 120, sortValue: (r) => Number(r.value || 0) },
                ]}
                footer={byProvider.length >= 50 ? 'Showing the 50 providers with the most errors.' : null}
              />
            </SectionCard>
          </div>

          <SectionCard title="Re-claim outcomes" icon={RefreshCw}>
            <DataTable
              label="Re-claim outcomes by attempt" rows={attempts} rowKey={(r) => r.attempt}
              empty={{ icon: Inbox, title: 'Nothing has been re-claimed yet', hint: 'Attempts appear here once a corrected invoice goes back to the NDIA in a new batch.' }}
              columns={[
                { key: 'attempt', header: 'Attempt', width: 110, render: (r) => <span className="tabular-nums font-semibold text-slate-800">{Number(r.attempt) === 1 ? 'First claim' : `Retry ${Number(r.attempt) - 1}`}</span> },
                { key: 'lines', header: 'Lines', align: 'right', width: 100, render: (r) => <span className="tabular-nums">{int(r.lines)}</span> },
                { key: 'succeeded', header: 'Paid or short paid', align: 'right', width: 160, render: (r) => <span className="tabular-nums text-emerald-600 font-semibold">{int(r.succeeded)}</span> },
                { key: 'failed', header: 'Rejected or duplicate', align: 'right', width: 175, render: (r) => <span className={clsx('tabular-nums font-semibold', Number(r.failed) ? 'text-rose-600' : 'text-slate-400')}>{int(r.failed)}</span> },
                {
                  // A high share here is the good outcome, so the default "large share = suspicious"
                  // colouring is overridden: emerald when the retry worked, rose when it did not.
                  key: 'rate', header: 'Success', align: 'right', width: 130,
                  render: (r) => {
                    const lines = Number(r.lines || 0);
                    const rate = lines > 0 ? Number(r.succeeded || 0) / lines : 0;
                    return <ShareBar value={r.succeeded} total={lines} tone={rate >= 0.8 ? 'emerald' : rate >= 0.5 ? 'amber' : 'rose'} />;
                  },
                },
              ]}
            />
          </SectionCard>
        </div>
      )}
    </Panel>
  );
}

/* ── 4 · Provider payments ─────────────────────────────────────────────────
   `total_held` is the cost of not finishing verification: money the NDIA has
   already paid you that cannot leave for the provider because their bank or
   ABN details are unverified. That is the hero; the batch register is support. */

function ProviderPaymentsPanel() {
  const { loading, error, data, reload } = useReport(`${PM}/reports/provider-payments`);
  const batches = (data && data.batches) || [];
  const blocked = (data && data.blocked_providers) || { count: 0, total_held: 0, providers: [] };

  const batchTone = (b) => (b.status === 'generated' ? 'amber' : b.status === 'processed' && !b.remittance_sent ? 'amber' : undefined);

  return (
    <Panel
      title="Provider payments"
      actions={(
        <ExportButton filename={`pm-blocked-providers-${new Date().toISOString().slice(0, 10)}.csv`} rows={blocked.providers || []}
          label="Export blocked"
          columns={[
            { header: 'Provider', value: 'name' },
            { header: 'Bank verified', value: (r) => (r.bank_verified === true ? 'Yes' : 'No') },
            { header: 'ABN status', value: 'abn_status' },
            { header: 'Record status', value: 'status' },
            { header: 'Invoices waiting', value: 'waiting_invoices' },
            { header: 'Value held', value: 'waiting_value' },
          ]} />
      )}
      blurb="Payment batches with their remittance state, and the providers whose money cannot leave because their details are not verified."
      loading={loading} error={error} onRetry={reload}
      isEmpty={!!data && !batches.length && !blocked.count}
      empty={{ title: 'No payment activity yet', hint: 'Batches appear here once reconciled invoices are paid out to providers.' }}>
      {data && (
        <div className="space-y-4">
          <Hero
            icon={Banknote}
            label="Held — provider details unverified"
            value={money(blocked.total_held)}
            tone={blocked.total_held > 0 ? 'rose' : 'emerald'}
            sub={blocked.count
              ? `Reconciled and ready to pay, but stuck across ${int(blocked.count)} ${blocked.count === 1 ? 'provider' : 'providers'} with unverified bank details or an incomplete record.`
              : 'Every provider with money waiting has verified bank details.'}
            hint="This is the cost of not finishing verification: the NDIA has paid, the participant's funds are committed, and the provider still has not been paid."
            aside={(
              <StatRow cols={2} className="min-w-[14rem]">
                <MoneyStat label="Awaiting the bank" value={int(data.awaiting_bank)} currency={false}
                  tone={data.awaiting_bank ? 'amber' : 'slate'} sub="file generated, not processed" />
                <MoneyStat label="Awaiting remittance" value={int(data.awaiting_remittance)} currency={false}
                  tone={data.awaiting_remittance ? 'amber' : 'slate'} sub="paid, provider not told" />
              </StatRow>
            )}
          />

          <SectionCard title={`Blocked providers · ${int(blocked.count)}`} icon={AlertTriangle}>
            <DataTable
              label="Providers whose payments are blocked"
              rows={blocked.providers || []} rowTone={() => 'rose'}
              defaultSort={{ key: 'waiting_value', dir: 'desc' }}
              maxHeight="24rem"
              empty={{ icon: CheckCircle2, title: 'No provider is blocked', hint: 'Bank details are verified everywhere money is waiting.' }}
              columns={[
                { key: 'name', header: 'Provider', sortable: true, truncate: true, render: (r) => r.name || <span className="text-slate-300">Unnamed</span> },
                { key: 'bank_verified', header: 'Bank', width: 130, render: (r) => (r.bank_verified ? <Pill tone="emerald">Verified</Pill> : <Pill tone="rose">Not verified</Pill>) },
                { key: 'abn_status', header: 'ABN', width: 130, render: (r) => (r.abn_status ? <Pill tone={String(r.abn_status).toLowerCase() === 'active' ? 'emerald' : 'amber'}>{r.abn_status}</Pill> : <span className="text-slate-300">—</span>) },
                { key: 'status', header: 'Record', width: 130, render: (r) => (r.status === 'complete' ? <Pill tone="emerald">Complete</Pill> : <Pill tone="amber">{r.status || 'Incomplete'}</Pill>) },
                { key: 'waiting_invoices', header: 'Invoices', align: 'right', sortable: true, width: 100, render: (r) => <span className="tabular-nums">{int(r.waiting_invoices)}</span> },
                { key: 'waiting_value', header: 'Held', money: true, sortable: true, width: 130, sortValue: (r) => Number(r.waiting_value || 0) },
              ]}
              footer={blocked.count ? (
                <span className="flex items-center gap-2">
                  <span className="tabular-nums font-semibold text-rose-600">{money(blocked.total_held)}</span>
                  <span className="text-slate-400">cannot be paid until these records are verified</span>
                </span>
              ) : null}
            />
          </SectionCard>

          <SectionCard title={`Payment batches · ${int(batches.length)}`} icon={Banknote}>
            <DataTable
              label="Provider payment batches" rows={batches} rowTone={batchTone}
              maxHeight="30rem"
              empty={{ icon: Inbox, title: 'No batches yet', hint: 'A batch is created when reconciled invoices are paid out.' }}
              columns={[
                { key: 'batch_ref', header: 'Batch', sortable: true, width: 170, render: (r) => <span className="font-mono text-[12px] text-slate-700">{r.batch_ref || String(r.id || '').slice(0, 8)}</span> },
                {
                  key: 'status', header: 'Status', sortable: true, width: 140,
                  render: (r) => <Pill tone={r.status === 'processed' ? 'emerald' : r.status === 'generated' ? 'amber' : 'slate'}>{r.status || '—'}</Pill>,
                },
                { key: 'payment_count', header: 'Payments', align: 'right', sortable: true, width: 100, render: (r) => <span className="tabular-nums">{int(r.payment_count)}</span> },
                { key: 'invoice_count', header: 'Invoices', align: 'right', sortable: true, width: 100, render: (r) => <span className="tabular-nums">{int(r.invoice_count)}</span> },
                { key: 'total_amount', header: 'Total', money: true, sortable: true, width: 130, sortValue: (r) => Number(r.total_amount || 0) },
                {
                  key: 'remittance_sent', header: 'Remittance', width: 130,
                  render: (r) => (r.remittance_sent ? <Pill tone="emerald">Sent</Pill> : r.status === 'processed' ? <Pill tone="amber">Not sent</Pill> : <span className="text-slate-300">—</span>),
                },
                { key: 'bank_reference', header: 'Bank ref', truncate: true, render: (r) => (r.bank_reference ? <span className="font-mono text-[12px] text-slate-500">{r.bank_reference}</span> : <span className="text-slate-300">—</span>) },
                { key: 'processed_at', header: 'Processed', sortable: true, width: 140, render: (r) => <span className="text-slate-500">{r.processed_at ? fmtDate(r.processed_at) : <span className="text-slate-300">—</span>}</span> },
              ]}
              footer={batches.length >= 100 ? 'Showing the 100 most recent batches.' : null}
            />
          </SectionCard>
        </div>
      )}
    </Panel>
  );
}

/* ── 5 · Missing evidence ──────────────────────────────────────────────────
   `value_at_risk` is money already claimed or paid with no invoice document
   behind it. The NDIA requires the invoice behind every claim to be
   produceable, so this is a record-keeping exposure — it leads. */

const EVIDENCE_CAP = 300;
const EVIDENCE_EXPOSED = ['claimed', 'paid', 'provider_paid'];

function MissingEvidencePanel() {
  const { loading, error, data, reload } = useReport(`${PM}/reports/missing-evidence`);
  // Exposed rows first: the ones already claimed or paid are the ones that must be chased today,
  // and a waived row drops below an unwaived one at the same exposure.
  const ordered = useMemo(() => {
    const all = (data && data.invoices) || [];
    const rank = (r) => (EVIDENCE_EXPOSED.includes(r.status) ? 0 : 2) + (r.evidence_waived_at ? 1 : 0);
    return [...all].sort((a, b) => rank(a) - rank(b) || Number(b.total_amount || 0) - Number(a.total_amount || 0));
  }, [data]);
  const shown = ordered.slice(0, EVIDENCE_CAP);
  const hidden = ordered.length - shown.length;

  return (
    <Panel
      title="Missing evidence"
      actions={(
        <ExportButton filename={`pm-missing-evidence-${new Date().toISOString().slice(0, 10)}.csv`} rows={ordered}
          columns={[
            { header: 'Invoice', value: 'provider_invoice_number' },
            { header: 'Participant', value: 'participant' },
            { header: 'Provider', value: 'provider' },
            { header: 'Invoice date', value: 'invoice_date' },
            { header: 'Status', value: 'status' },
            { header: 'Amount', value: 'total_amount' },
            { header: 'Waived at', value: 'evidence_waived_at' },
          ]} />
      )}
      blurb="Invoices with no document attached. Plan-management fee invoices are excluded — the system generates and documents those itself."
      loading={loading} error={error} onRetry={reload}
      isEmpty={!!data && !data.count}
      empty={{ title: 'Every invoice has its document', hint: 'Nothing has been claimed or paid without the provider invoice behind it.' }}>
      {data && (
        <div className="space-y-4">
          <Hero
            icon={FileWarning}
            label="Value at risk"
            value={money(data.value_at_risk)}
            tone={Number(data.value_at_risk) > 0 ? 'rose' : 'amber'}
            sub={data.already_claimed_or_paid
              ? `${int(data.already_claimed_or_paid)} invoices have already been claimed or paid with no invoice document on file.`
              : 'Nothing claimed or paid is missing its document — the gaps below are all still upstream of a claim.'}
            hint="The NDIA requires the invoice behind every claim to be produceable. Obtain the provider invoice and attach it, or record a waiver with a reason."
            aside={(
              <StatRow cols={3} className="min-w-[16rem]">
                <MoneyStat label="Missing a document" value={int(data.count)} currency={false} tone={data.count ? 'amber' : 'emerald'} />
                <MoneyStat label="Already claimed or paid" value={int(data.already_claimed_or_paid)} currency={false}
                  tone={data.already_claimed_or_paid ? 'rose' : 'emerald'} />
                <MoneyStat label="Waived" value={int(data.waived)} currency={false} sub="with a recorded reason" />
              </StatRow>
            )}
          />

          <SectionCard title={`Invoices with no document · ${int(data.count)}`} icon={FileWarning}>
            <DataTable
              label="Invoices with no evidence document"
              rows={shown}
              rowTone={(r) => (EVIDENCE_EXPOSED.includes(r.status) && !r.evidence_waived_at ? 'rose' : r.evidence_waived_at ? undefined : 'amber')}
              maxHeight="34rem"
              empty={{ icon: CheckCircle2, title: 'Nothing missing' }}
              columns={[
                { key: 'provider_invoice_number', header: 'Invoice', sortable: true, width: 150, render: (r) => r.provider_invoice_number || <span className="text-slate-300">—</span> },
                { key: 'participant', header: 'Participant', sortable: true, truncate: true },
                { key: 'provider', header: 'Provider', sortable: true, truncate: true, render: (r) => r.provider || <span className="text-slate-300">—</span> },
                { key: 'invoice_date', header: 'Invoice date', sortable: true, width: 130, render: (r) => <span className="text-slate-500">{fmtDate(r.invoice_date)}</span> },
                { key: 'status', header: 'Status', sortable: true, width: 155, render: (r) => <StatusBadge status={r.status} /> },
                { key: 'total_amount', header: 'Amount', money: true, sortable: true, width: 120, sortValue: (r) => Number(r.total_amount || 0) },
                {
                  key: 'evidence_waived_at', header: 'Waiver', width: 180, truncate: true,
                  render: (r) => (r.evidence_waived_at ? (
                    <span className="inline-flex items-center gap-1.5 min-w-0"
                      title={`Waived ${fmtDateTime(r.evidence_waived_at)}${r.waived_by ? ` by ${r.waived_by}` : ''}${r.evidence_waived_reason ? ` — ${r.evidence_waived_reason}` : ''}`}>
                      <Pill tone="slate">Waived</Pill>
                      <span className="text-[11.5px] text-slate-400 truncate">{r.evidence_waived_reason || fmtDate(r.evidence_waived_at)}</span>
                    </span>
                  ) : <span className="text-slate-300">—</span>),
                },
              ]}
              footer={hidden > 0 ? `Showing the ${int(EVIDENCE_CAP)} most exposed of ${int(ordered.length)} — claimed and paid invoices are listed first.` : null}
            />
          </SectionCard>

          <Footnote>{data.note}</Footnote>
        </div>
      )}
    </Panel>
  );
}

/* ── 6 · Budget reconciliation ─────────────────────────────────────────────
   The spec 4.3 changeover: committed funding now counts unapproved, on-hold and
   claim-error invoices too, and availability is measured against released-to-
   date. Both make Available now smaller and more truthful. A plan flagged
   now_overcommitted was ALREADY overspent — the old figures were hiding it —
   so that flag, not the delta, is what has to be visible. */

function BudgetReconciliationPanel() {
  const { loading, error, data, reload } = useReport(`${PM}/budget-reconciliation`);
  const plans = (data && data.plans) || [];
  const over = Number((data && data.now_overcommitted) || 0);
  const worstDelta = plans.reduce((m, p) => Math.min(m, Number(p.delta || 0)), 0);

  return (
    <Panel
      title="Budget reconciliation"
      actions={(
        <ExportButton filename={`pm-budget-reconciliation-${new Date().toISOString().slice(0, 10)}.csv`} rows={plans}
          columns={[
            { header: 'Participant', value: 'participant' },
            { header: 'Plan', value: 'plan_ref' },
            { header: 'Budget', value: 'budget' },
            { header: 'Was available', value: 'was_available' },
            { header: 'Now available', value: 'now_available' },
            { header: 'Change', value: 'delta' },
          ]} />
      )}
      blurb="Every plan whose available balance moves under the corrected commitment rules, so the team can review before acting on the new numbers."
      loading={loading} error={error} onRetry={reload}
      isEmpty={!!data && !plans.length}
      empty={{ title: 'No plan changes', hint: data ? `${int(data.plans_reviewed)} plans reviewed and every available balance is unchanged.` : undefined }}>
      {data && (
        <div className="space-y-4">
          <Hero
            icon={Scale}
            label="Plans now overcommitted"
            value={int(over)}
            tone={over ? 'rose' : 'emerald'}
            sub={over
              ? `${int(over)} of ${int(data.plans_changed)} changed plans are committed past what has been released. They were already overspent — the old figures were hiding it.`
              : `${int(data.plans_changed)} plans changed and none is overcommitted.`}
            hint="Available now counts unapproved, on-hold and claim-error invoices, and instalment plans are measured against released-to-date rather than the whole allocation."
            aside={(
              <StatRow cols={3} className="min-w-[16rem]">
                <MoneyStat label="Plans reviewed" value={int(data.plans_reviewed)} currency={false} />
                <MoneyStat label="Plans changed" value={int(data.plans_changed)} currency={false} tone={data.plans_changed ? 'amber' : 'emerald'} />
                <MoneyStat label="Largest fall" value={worstDelta} tone={worstDelta < 0 ? 'rose' : 'slate'} sub="in available now" />
              </StatRow>
            )}
          />

          {over > 0 && (
            <Callout tone="rose" title="Review these before releasing anything further">
              An overcommitted plan has more invoiced against it than has been released. Check the funding
              periods and the on-hold invoices on each before approving or claiming any more of it.
            </Callout>
          )}

          <SectionCard title={`Changed plans · ${int(plans.length)}`} icon={Scale}>
            <DataTable
              label="Plans whose available balance changed"
              rows={plans} rowKey="plan_id"
              rowTone={(p) => (p.now_overcommitted ? 'rose' : Number(p.delta) < 0 ? 'amber' : undefined)}
              defaultSort={{ key: 'delta', dir: 'asc' }}
              maxHeight="34rem"
              empty={{ icon: CheckCircle2, title: 'No plan moved' }}
              columns={[
                {
                  key: 'participant', header: 'Participant', sortable: true, truncate: true,
                  render: (p) => (
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span className="truncate text-slate-800">{p.participant}</span>
                      {p.now_overcommitted && <span className="badge badge-red !text-[10px] shrink-0">Overcommitted</span>}
                    </span>
                  ),
                },
                { key: 'budget', header: 'Budget', money: true, sortable: true, width: 120, sortValue: (p) => Number(p.budget || 0) },
                {
                  key: 'released_to_date', header: 'Released to date', money: true, sortable: true, width: 150,
                  sortValue: (p) => Number(p.released_to_date || 0),
                  render: (p) => (Number(p.periods) > 0
                    ? <Currency value={p.released_to_date} />
                    : <span className="text-slate-300" title="No funding periods — availability is measured against the whole allocation.">whole plan</span>),
                },
                {
                  key: 'committed', header: 'Committed  before → after', align: 'right', width: 210,
                  render: (p) => (
                    <span className="tabular-nums whitespace-nowrap">
                      <span className="text-slate-400">{money(p.committed_before)}</span>
                      <span className="text-slate-300 mx-1.5">→</span>
                      <span className="text-slate-800 font-medium">{money(p.committed_after)}</span>
                    </span>
                  ),
                },
                {
                  key: 'available', header: 'Available  before → after', align: 'right', width: 210,
                  render: (p) => (
                    <span className="tabular-nums whitespace-nowrap">
                      <span className="text-slate-400">{money(p.available_before)}</span>
                      <span className="text-slate-300 mx-1.5">→</span>
                      <span className={clsx('font-semibold', Number(p.available_after) < 0 ? 'text-rose-600' : 'text-slate-800')}>{money(p.available_after)}</span>
                    </span>
                  ),
                },
                {
                  key: 'delta', header: 'Change', align: 'right', sortable: true, width: 130, defaultDir: 'asc',
                  sortValue: (p) => Number(p.delta || 0),
                  render: (p) => (
                    <span className={clsx('tabular-nums font-semibold', Number(p.delta) < 0 ? 'text-rose-600' : 'text-emerald-600')}>
                      {Number(p.delta) > 0 ? '+' : ''}{money(p.delta)}
                    </span>
                  ),
                },
              ]}
            />
          </SectionCard>

          <Footnote>{data.note}</Footnote>
        </div>
      )}
    </Panel>
  );
}

/* ── 7 · Audit export ──────────────────────────────────────────────────────
   The append-only trail, narrowed. The route refuses an unfiltered export (it
   would be the whole org's history), so the form enforces the same rule before
   the request is sent rather than letting the operator collect a 400. */

const AUDIT_CAP = 500;
const EMPTY_FILTERS = { client_id: '', provider_id: '', invoice_id: '', from: '', to: '' };
// Trailing chars of a UUID vary, so this only checks the shape the route's isUuid() accepts.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function AuditExportPanel() {
  const [f, setF] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(null);   // snapshot of the filters actually run
  const [nonce, setNonce] = useState(0);      // re-run the same query — the trail may have grown
  const [downloading, setDownloading] = useState(false);
  const [people, setPeople] = useState({ participants: [], providers: [] });

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get(`${PM}/participants`).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
      api.get(`${PM}/providers`).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
    ]).then(([participants, providers]) => { if (live) setPeople({ participants, providers }); });
    return () => { live = false; };
  }, []);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  // Mirrors the route's own guard: a participant, provider, invoice or a from-date.
  const invoiceIdBad = !!f.invoice_id && !UUID_RE.test(f.invoice_id.trim());
  const narrowed = !!(f.client_id || f.provider_id || (f.invoice_id && !invoiceIdBad) || f.from);
  const dirty = Object.keys(EMPTY_FILTERS).some((k) => f[k] !== EMPTY_FILTERS[k]);
  const params = useMemo(() => ({
    client_id: f.client_id || undefined,
    provider_id: f.provider_id || undefined,
    invoice_id: (f.invoice_id && !invoiceIdBad) ? f.invoice_id.trim() : undefined,
    from: f.from || undefined,
    to: f.to || undefined,
  }), [f, invoiceIdBad]);

  const { loading, error, data, reload } = useReport(`${PM}/reports/audit-export`, { params: query || {}, enabled: !!query, nonce });

  const run = (e) => {
    if (e) e.preventDefault();
    if (!narrowed) return;
    setQuery(params);
    setNonce((n) => n + 1);
  };

  const downloadCsv = async () => {
    if (!narrowed || downloading) return;
    setDownloading(true);
    try {
      const r = await api.get(`${PM}/reports/audit-export`, { params: { ...params, format: 'csv' }, responseType: 'blob' });
      downloadBlob(r.data, `pm-audit-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Audit trail exported');
    } catch (err) {
      toast.error(await blobMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const entries = (data && data.entries) || [];
  const shown = entries.slice(0, AUDIT_CAP);

  return (
    <Panel
      title="Audit export"
      blurb="The append-only trail for one participant, provider, invoice or date range — who did what, when. This is what gets produced for an NDIA or NDIS Commission request.">
      <div className="space-y-4">
        <SectionCard title="Narrow the export" icon={FileSearch} bodyClass="px-5 py-4">
          <form onSubmit={run} className="space-y-4">
            <FormRow cols={3}>
              <Field label="Participant" hint={people.participants.length ? undefined : 'No plan-managed participants loaded'}>
                {(id) => (
                  <select id={id} value={f.client_id} onChange={set('client_id')} className="input-field">
                    <option value="">Any participant</option>
                    {people.participants.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ndis_number ? ` · ${p.ndis_number}` : ''}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Provider">
                {(id) => (
                  <select id={id} value={f.provider_id} onChange={set('provider_id')} className="input-field">
                    <option value="">Any provider</option>
                    {people.providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Invoice ID" error={invoiceIdBad ? 'That is not a valid invoice ID.' : null}
                hint={invoiceIdBad ? undefined : 'Paste the ID from an invoice record'}>
                {(id) => (
                  <input id={id} type="text" value={f.invoice_id} onChange={set('invoice_id')} placeholder="Any invoice"
                    className={clsx('input-field font-mono !text-[12px]', invoiceIdBad && '!border-rose-300')} />
                )}
              </Field>
            </FormRow>
            <FormRow cols={3}>
              <Field label="From" hint="A from-date alone is enough to narrow the export">
                {(id) => <input id={id} type="date" value={f.from} max={f.to || undefined} onChange={set('from')} className="input-field" />}
              </Field>
              <Field label="To">
                {(id) => <input id={id} type="date" value={f.to} min={f.from || undefined} onChange={set('to')} className="input-field" />}
              </Field>
              <div className="flex items-end gap-2">
                <button type="submit" disabled={!narrowed || loading} className="btn-primary btn-sm">
                  {loading ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}Run
                </button>
                <button type="button" onClick={downloadCsv} disabled={!narrowed || downloading} className="btn-secondary btn-sm">
                  {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}Download CSV
                </button>
                {(query || dirty) && (
                  <button type="button" className="btn-ghost btn-sm" onClick={() => { setF(EMPTY_FILTERS); setQuery(null); }}>Clear</button>
                )}
              </div>
            </FormRow>
            {!narrowed && (
              <Footnote>
                Choose a participant, a provider, an invoice or a from-date. An unfiltered export would be the
                whole organisation's history, so the server refuses it.
              </Footnote>
            )}
          </form>
        </SectionCard>

        {loading ? (
          <SectionCard><TableSkeleton rows={8} cols={6} /></SectionCard>
        ) : error ? (
          <SectionCard><ErrorState message={error} onRetry={reload} /></SectionCard>
        ) : !data ? (
          <SectionCard>
            <EmptyState icon={FileSearch} title="Nothing exported yet"
              hint="Set a filter above, then Run to preview the trail on screen or Download CSV for the full file." />
          </SectionCard>
        ) : (
          <React.Fragment>
            {data.truncated && (
              <Callout title="Capped at 20,000 entries">
                The trail for this filter is longer than the export limit. Narrow the date range and run it in
                parts, or the record produced will be incomplete.
              </Callout>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiTile label="Entries" value={int(data.count)} accent="slate" icon={FileSearch} />
              <KpiTile label="Shown on screen" value={int(shown.length)} accent="slate"
                sub={entries.length > AUDIT_CAP ? 'download the CSV for the rest' : 'the full result'} />
              <KpiTile label="From" value={f.from ? fmtDate(f.from) : 'Any'} accent="slate" />
              <KpiTile label="To" value={f.to ? fmtDate(f.to) : 'Any'} accent="slate" />
            </div>

            <SectionCard title="Trail" icon={FileSearch}>
              <DataTable
                label="Audit trail entries" rows={shown} rowKey={(r, i) => `${r.created_at}-${i}`}
                maxHeight="36rem" dense
                empty={{ icon: Inbox, title: 'No entries for this filter', hint: 'Nothing was recorded against this participant, provider, invoice or date range.' }}
                columns={[
                  { key: 'created_at', header: 'When', sortable: true, width: 175, render: (r) => <span className="text-slate-500 tabular-nums whitespace-nowrap">{fmtDateTime(r.created_at)}</span> },
                  { key: 'participant', header: 'Participant', sortable: true, truncate: true, render: (r) => r.participant || <span className="text-slate-300">—</span> },
                  { key: 'provider', header: 'Provider', sortable: true, truncate: true, render: (r) => r.provider || <span className="text-slate-300">—</span> },
                  { key: 'provider_invoice_number', header: 'Invoice', width: 130, render: (r) => r.provider_invoice_number || <span className="text-slate-300">—</span> },
                  { key: 'entity', header: 'Entity', sortable: true, width: 110, render: (r) => <span className="text-slate-500">{r.entity || '—'}</span> },
                  { key: 'action', header: 'Action', sortable: true, width: 180, render: (r) => <span className="font-medium text-slate-800">{String(r.action || '').replace(/_/g, ' ')}</span> },
                  { key: 'actor', header: 'By', sortable: true, width: 150, render: (r) => r.actor || <span className="text-slate-300">System</span> },
                  {
                    key: 'detail', header: 'Detail', truncate: true,
                    render: (r) => {
                      const d = r.detail && Object.keys(r.detail).length ? JSON.stringify(r.detail) : '';
                      return d ? <span className="font-mono text-[11px] text-slate-500" title={d}>{d}</span> : <span className="text-slate-300">—</span>;
                    },
                  },
                ]}
                footer={entries.length > AUDIT_CAP
                  ? `Previewing the first ${int(AUDIT_CAP)} of ${int(data.count)} entries. Download the CSV for the complete trail.`
                  : null}
              />
            </SectionCard>
          </React.Fragment>
        )}
      </div>
    </Panel>
  );
}

/* ── shell ─────────────────────────────────────────────────────────────── */

// Exported so a parent can deep-link a panel (?report=aged) without re-declaring the list.
export const PM_REPORTS = [
  { id: 'turnaround', label: 'Turnaround', icon: Timer, blurb: 'Business days per stage against the provider commitment', Panel: TurnaroundPanel },
  { id: 'aged', label: 'Aged work', icon: Hourglass, blurb: 'Approved but unclaimed, and claims with no NDIA result', Panel: AgedPanel },
  { id: 'claim-errors', label: 'Claim errors', icon: AlertOctagon, blurb: 'Grouped by reason and provider to expose a systemic fault', Panel: ClaimErrorsPanel },
  { id: 'provider-payments', label: 'Provider payments', icon: Banknote, blurb: 'Batches, remittance, and money held by unverified details', Panel: ProviderPaymentsPanel },
  { id: 'missing-evidence', label: 'Missing evidence', icon: FileWarning, blurb: 'Claims with no invoice document behind them', Panel: MissingEvidencePanel },
  { id: 'budget-reconciliation', label: 'Budget reconciliation', icon: Scale, blurb: 'Plans whose available balance moved, and who is overcommitted', Panel: BudgetReconciliationPanel },
  { id: 'audit-export', label: 'Audit export', icon: FileSearch, blurb: 'The trail produced for an NDIA or Commission request', Panel: AuditExportPanel },
];

export default function Reports({ report, onReportChange, defaultReport = 'turnaround' }) {
  const [internal, setInternal] = useState(defaultReport);
  // Controlled when the parent passes `report` (so it can own the URL), uncontrolled otherwise.
  const activeId = report || internal;
  const active = PM_REPORTS.find((r) => r.id === activeId) || PM_REPORTS[0];
  const select = (id) => { setInternal(id); if (onReportChange) onReportChange(id); };
  const ActivePanel = active.Panel;

  return (
    <div className="flex flex-col lg:flex-row items-start gap-5">
      {/* Below lg the rail becomes one scrolling row of chips — a seven-item vertical
          list above every panel would push the hero figure off the first screen. */}
      <nav aria-label="Reports" className="w-full lg:w-64 lg:shrink-0 lg:sticky lg:top-4">
        <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 -mx-1 px-1">
          {PM_REPORTS.map((r) => {
            const on = r.id === active.id;
            const Icon = r.icon;
            return (
              <button
                key={r.id} type="button" onClick={() => select(r.id)} aria-current={on ? 'page' : undefined}
                className={clsx('shrink-0 lg:shrink text-left rounded-xl border px-3 py-2.5 transition-colors motion-reduce:transition-none',
                  'flex items-start gap-2.5 min-w-0',
                  on ? 'border-primary-300 bg-primary-50' : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300')}>
                <Icon size={15} className={clsx('mt-0.5 shrink-0', on ? 'text-primary-600' : 'text-slate-400')} />
                <span className="min-w-0">
                  <span className={clsx('block text-[13px] font-semibold whitespace-nowrap lg:whitespace-normal', on ? 'text-primary-700' : 'text-slate-700')}>{r.label}</span>
                  <span className="hidden lg:block text-[11px] text-slate-400 mt-0.5 leading-snug">{r.blurb}</span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Keyed so switching reports remounts the panel — a stale hero figure from the
          previous report flashing under the new title is worse than a skeleton. */}
      <div className="min-w-0 flex-1 w-full">
        <ActivePanel key={active.id} />
      </div>
    </div>
  );
}
